/**
 * Verifica se o HealthcareService do PA está disponível na hora atual.
 * A jornada PA é só do dia corrente (buildProntoHealthcareServicePayload).
 */
import dayjs from "dayjs";
import { endpoints } from "@/configs/Settings";
import { getAuthToken } from "@/services/auth/OAuthApplication";
import { HTTPResponseStatus } from "@/utils/enum";
import { useFormatter } from "@/utils/useFormatter";
import { useAxios } from "@/utils/useAxios";
import { getTodayPtDayLabel } from "./buildProntoHealthcareServicePayload";

/** Tolerância padrão do PA interno (profissional + clínica), após o fim configurado. */
export const PRONTO_ATENDIMENTO_INTERNAL_TOLERANCE_MINUTES = 10;

/** serviceModel da URL pública para contexto exclusivo de PA. */
export const PRONTO_ATENDIMENTO_PUBLIC_SERVICE_MODEL = "prontoAtendimento";

const hcsCache = new Map<string, fhir5.HealthcareService>();

/** Extrai id do actor HealthcareService/ do Schedule. */
export function getHealthcareServiceIdFromSchedule(
  schedule: fhir5.Schedule,
): string | undefined {
  const actor = schedule.actor?.find((a) =>
    a.reference?.includes("HealthcareService/"),
  );
  return actor?.reference?.split("/")?.[1];
}

/** Extrai HealthcareServices incluídos em um Bundle de Schedule. */
export function extractHealthcareServicesFromBundle(
  bundle: fhir5.Bundle | undefined,
): Map<string, fhir5.HealthcareService> {
  const map = new Map<string, fhir5.HealthcareService>();
  bundle?.entry?.forEach((entry) => {
    const res = entry.resource;
    if (res?.resourceType === "HealthcareService" && res.id) {
      map.set(res.id, res as fhir5.HealthcareService);
    }
  });
  return map;
}

/** Busca HealthcareService por id (auth de aplicação — página pública). */
export async function fetchHealthcareServiceById(
  id: string,
): Promise<fhir5.HealthcareService | undefined> {
  const cached = hcsCache.get(id);
  if (cached) return cached;

  const url = `${endpoints.WORKFLOWFHIR}/HealthcareService/${id}`;
  const headers = {
    Accept: "*/*",
    Authorization: await getAuthToken(),
    "Content-Type": "application/fhir+json",
  };

  try {
    const response = await useAxios.get(url, { headers, timeout: 20000 });
    if (response.status !== HTTPResponseStatus.Success) return undefined;
    const resource = response.data as fhir5.HealthcareService;
    if (resource?.resourceType === "HealthcareService") {
      hcsCache.set(id, resource);
      return resource;
    }
  } catch (error) {
    console.error("fetchHealthcareServiceById", id, error);
  }
  return undefined;
}

/** Normaliza daysOfWeek do FHIR (string | array | undefined). */
function normalizeDaysOfWeek(daysOfWeek: unknown): string[] {
  if (!daysOfWeek) return [];
  const list = Array.isArray(daysOfWeek) ? daysOfWeek : [daysOfWeek];
  return list
    .map((d) => String(d).trim().toLowerCase())
    .filter(Boolean);
}

/** Converte horário FHIR ("HH:mm", "HH:mm:ss" ou ISO) em minutos. */
function timeToMinutes(time: string | undefined): number | null {
  if (!time) return null;
  let raw = time.trim();
  if (raw.includes("T")) {
    raw = raw.split("T")[1]?.split(/[+-Z]/)[0] ?? raw;
  }
  const parts = raw.split(":").map((p) => parseInt(p, 10));
  if (!Number.isFinite(parts[0])) return null;
  const h = parts[0];
  const m = Number.isFinite(parts[1]) ? parts[1] : 0;
  return h * 60 + m;
}

/** Dia FHIR (mon, tue, …) correspondente a hoje. */
function getTodayFhirDay(): string {
  const ptDay = getTodayPtDayLabel();
  return useFormatter.translateWeekDays(ptDay).toLowerCase();
}

/** Coleta availableTime tolerando availability objeto ou array (HAPI/FHIR). */
export function getHealthcareServiceAvailableTimes(
  hcs: fhir5.HealthcareService | undefined,
): fhir5.AvailabilityAvailableTime[] {
  if (!hcs?.availability) return [];

  const blocks = Array.isArray(hcs.availability)
    ? hcs.availability
    : [hcs.availability];

  return blocks.flatMap((block) => block.availableTime ?? []);
}

/** Há janela de horário configurada para o dia de hoje. */
export function hasProntoHealthcareServiceTimeWindowForToday(
  hcs: fhir5.HealthcareService | undefined,
): boolean {
  const todayFhirDay = getTodayFhirDay();
  const availableTimes = getHealthcareServiceAvailableTimes(hcs);
  return availableTimes.some((slot) => {
    const days = normalizeDaysOfWeek(slot.daysOfWeek);
    if (days.length > 0 && !days.includes(todayFhirDay)) return false;
    const start = timeToMinutes(slot.availableStartTime);
    const end = timeToMinutes(slot.availableEndTime);
    return start !== null && end !== null;
  });
}

/**
 * Dentro da janela de hoje: start <= now <= end + toleranceMinutes.
 * toleranceMinutes: grace após o fim configurado (PA interno usa 10 min).
 */
export function isProntoHealthcareServiceWithinWindow(
  hcs: fhir5.HealthcareService | undefined,
  at: dayjs.Dayjs = dayjs(),
  toleranceMinutes = 0,
): boolean {
  const todayFhirDay = getTodayFhirDay();
  const nowMinutes = at.hour() * 60 + at.minute();
  const availableTimes = getHealthcareServiceAvailableTimes(hcs);

  if (!availableTimes.length) return false;

  return availableTimes.some((slot) => {
    const days = normalizeDaysOfWeek(slot.daysOfWeek);
    if (days.length > 0 && !days.includes(todayFhirDay)) return false;

    const start = timeToMinutes(slot.availableStartTime);
    const end = timeToMinutes(slot.availableEndTime);
    if (start === null || end === null) return false;

    return nowMinutes >= start && nowMinutes <= end + toleranceMinutes;
  });
}

/**
 * Retorna true se a hora atual está dentro de alguma janela availableTime de hoje.
 * Alias sem tolerância extra (página pública).
 */
export function isProntoHealthcareServiceAvailableNow(
  hcs: fhir5.HealthcareService | undefined,
  at: dayjs.Dayjs = dayjs(),
): boolean {
  return isProntoHealthcareServiceWithinWindow(hcs, at, 0);
}

/** Limpa cache (útil em testes ou refresh manual). */
export function clearProntoHealthcareServiceCache(): void {
  hcsCache.clear();
}
